/**
 * Room detail · History tab.
 *
 * Three truthful, non-overlapping sections derived from the canonical
 * `classifyRoomBooking` source of truth (via `useRoomHistory`):
 *   - Upcoming bookings   — committed (active) tenancies that haven't started
 *   - Past tenants        — tenancies that actually happened and concluded:
 *                           the tenant moved in / paid the deposit, then left
 *   - Pending applications — bookings that never became a tenancy
 *
 * Applicants who never moved in or paid a deposit are deliberately kept out
 * of "Past tenants" (COL-251, COL-556) — they were never in the room, and
 * archived ones appear nowhere. The room's current tenant lives on the Current
 * tab — that includes a tenant who has given notice but not yet left — so it
 * is intentionally absent here.
 *
 * STATUS — every row shows the DERIVED journey stage, the same vocabulary the
 * tenant profile header and the Tenants pipeline filter use. It used to print
 * the raw `bookings.status` enum ("confirmed", "pending"), a word that appears
 * nowhere else in the product and that can claim a stage the evidence does not
 * support.
 */
import { Link } from "react-router-dom";
import { Clock, History } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { TenantJourneyStageBadge } from "@/components/admin/v2/tenant/TenantJourneyStageBadge";
import {
  effectiveEndDate,
  effectiveStartDate,
  useProfile,
  type BookingRow,
} from "@/hooks/v2/useProperties";
import { useRoomHistory } from "@/hooks/v2/useRoomHistory";
import type { TenantJourney } from "@/lib/tenantJourney";
import { formatDate, formatEUR } from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";

type Props = {
  propertyId: number;
  roomId: number;
  bookings: BookingRow[];
};

export function HistoryTab({ propertyId, roomId, bookings }: Props) {
  const { t } = useLanguage();
  // One resolve for the whole tab — the evidence rows are fetched in a single
  // batch rather than per row.
  const {
    upcoming,
    past,
    applications,
    journeys,
    journeysLoading,
    resolving,
    failed,
  } = useRoomHistory(bookings);

  if (
    !resolving &&
    !failed &&
    past.length === 0 &&
    upcoming.length === 0 &&
    applications.length === 0
  ) {
    return (
      <Card className="border-dashed">
        <EmptyState
          icon={History}
          title={t("admin.empty.noHistoryTitle", "No history yet")}
          description={t(
            "admin.empty.noHistoryDesc",
            "Past tenants and upcoming bookings for this room will show up here."
          )}
          compact
        />
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <Section
          title={t("admin.section.upcomingBookings", "Upcoming bookings")}
          bookings={upcoming}
          journeys={journeys}
          journeysLoading={journeysLoading}
          propertyId={propertyId}
          roomId={roomId}
          emptyMessage="No upcoming bookings."
        />
      )}
      <Section
        title={t("admin.section.pastTenants", "Past tenants")}
        bookings={past}
        loading={resolving}
        failed={failed}
        journeys={journeys}
        journeysLoading={journeysLoading}
        propertyId={propertyId}
        roomId={roomId}
        emptyMessage="No past tenants."
      />
      {!resolving && !failed && applications.length > 0 && (
        <Section
          title={t("admin.section.pendingApplications", "Pending applications")}
          bookings={applications}
          journeys={journeys}
          journeysLoading={journeysLoading}
          propertyId={propertyId}
          roomId={roomId}
          emptyMessage="No pending applications."
        />
      )}
    </div>
  );
}

function Section({
  title,
  bookings,
  loading = false,
  failed = false,
  journeys,
  journeysLoading,
  propertyId,
  roomId,
  emptyMessage,
}: {
  title: string;
  bookings: BookingRow[];
  /** Rows can't be trusted yet — show a placeholder instead of a count. */
  loading?: boolean;
  /** The evidence behind the rows could not be read — say so, don't show "none". */
  failed?: boolean;
  journeys: Map<number, TenantJourney>;
  journeysLoading: boolean;
  propertyId: number;
  roomId: number;
  emptyMessage: string;
}) {
  const { t } = useLanguage();
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
        <Clock className="h-3.5 w-3.5" />
        {title}
        {!loading && !failed && (
          <span className="font-normal opacity-60">({bookings.length})</span>
        )}
      </h2>
      {loading ? (
        <span
          aria-hidden
          className="block h-[58px] animate-pulse rounded-lg bg-muted"
        />
      ) : failed ? (
        // Reuses the existing owner-detail load-error string (EN + DE already
        // seeded) — a new key would need a translations migration.
        <p role="alert" className="text-xs text-destructive">
          {t("admin.ownerDetail.couldNotLoad", "Could not be loaded")}
        </p>
      ) : bookings.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">{emptyMessage}</p>
      ) : (
        <div className="space-y-1.5">
          {bookings.map((b) => (
            <BookingRowItem
              key={b.id}
              booking={b}
              journey={
                journeysLoading ? null : (journeys.get(Number(b.id)) ?? null)
              }
              propertyId={propertyId}
              roomId={roomId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BookingRowItem({
  booking,
  journey,
  propertyId,
  roomId,
}: {
  booking: BookingRow;
  journey: TenantJourney | null;
  propertyId: number;
  roomId: number;
}) {
  const { data: profile } = useProfile(booking.user_id);
  const start = effectiveStartDate(booking);
  const end = effectiveEndDate(booking);
  const displayName =
    profile?.name ??
    booking.name ??
    profile?.email ??
    booking.email ??
    "Tenant";
  const initials =
    (displayName === "Tenant" ? "T" : displayName)
      .split(/\s+/)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "T";

  return (
    <Link
      to={`/admin/properties/${propertyId}/rooms/${roomId}/tenants/${booking.id}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
    >
      <Card className="p-3 flex items-center gap-3 hover:shadow-sm transition-shadow">
        <Avatar className="h-9 w-9">
          <AvatarFallback className="text-xs">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground">
            {formatDate(start) ?? "—"} → {formatDate(end) ?? "—"}
          </p>
        </div>
        {journey ? (
          <TenantJourneyStageBadge
            stageId={journey.stageId}
            terminalStatus={journey.terminalStatus}
            archived={!!booking.admin_archived_at}
            archiveReason={booking.admin_archive_reason}
            className="shrink-0"
          />
        ) : (
          <span className="h-[22px] w-24 shrink-0 animate-pulse rounded-full bg-muted" />
        )}
        <span className="text-sm font-medium tabular-nums shrink-0">
          {formatEUR(booking.monthly_rent) ?? "—"}
        </span>
      </Card>
    </Link>
  );
}
